package com.example.socialmediaapp.ui.screens

import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.*
import androidx.compose.foundation.shape.*
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material.icons.outlined.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.*
import androidx.compose.ui.draw.*
import androidx.compose.ui.graphics.*
import androidx.compose.ui.text.font.*
import androidx.compose.ui.unit.*

// ============================================
// MAIN SETTINGS SCREEN
// ============================================

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SettingsScreen(
    onBack: () -> Unit,
    onLogout: () -> Unit
) {
    var showLogoutDialog by remember { mutableStateOf(false) }

    Scaffold(
        topBar = {
            CenterAlignedTopAppBar(
                title = {
                    Text(
                        "Settings",
                        fontWeight = FontWeight.SemiBold
                    )
                },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(Icons.Default.ArrowBack, "Back")
                    }
                },
                actions = {
                    TextButton(onClick = { /* Edit mode */ }) {
                        Text(
                            "Edit",
                            fontWeight = FontWeight.Medium
                        )
                    }
                }
            )
        }
    ) { padding ->
        LazyColumn(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
                .background(MaterialTheme.colorScheme.background),
            contentPadding = PaddingValues(vertical = 16.dp)
        ) {
            // Profile Section
            item {
                ProfileHeader()
                Spacer(Modifier.height(24.dp))
            }

            // Account Group
            item {
                SettingsGroup {
                    SettingsItem(
                        icon = Icons.Filled.Person,
                        iconColor = Color(0xFFE1306C),
                        title = "My Profile",
                        onClick = { }
                    )

                    SettingsItem(
                        icon = Icons.Filled.Bookmark,
                        iconColor = Color(0xFF2196F3),
                        title = "Saved Messages",
                        onClick = { }
                    )

                    SettingsItem(
                        icon = Icons.Filled.Phone,
                        iconColor = Color(0xFF4CAF50),
                        title = "Recent Calls",
                        onClick = { }
                    )

                    SettingsItem(
                        icon = Icons.Filled.Devices,
                        iconColor = Color(0xFFFF9800),
                        title = "Devices",
                        trailing = "2",
                        onClick = { }
                    )

                    SettingsItem(
                        icon = Icons.Filled.Folder,
                        iconColor = Color(0xFF00BCD4),
                        title = "Chat Folders",
                        onClick = { }
                    )
                }

                Spacer(Modifier.height(24.dp))
            }

            // Settings Group
            item {
                SettingsGroup {
                    SettingsItem(
                        icon = Icons.Filled.Notifications,
                        iconColor = Color(0xFFE1306C),
                        title = "Notifications and Sounds",
                        badge = true,
                        onClick = { }
                    )

                    SettingsItem(
                        icon = Icons.Filled.Lock,
                        iconColor = Color(0xFF9E9E9E),
                        title = "Privacy and Security",
                        onClick = { }
                    )

                    SettingsItem(
                        icon = Icons.Filled.Storage,
                        iconColor = Color(0xFF4CAF50),
                        title = "Data and Storage",
                        onClick = { }
                    )

                    SettingsItem(
                        icon = Icons.Filled.Palette,
                        iconColor = Color(0xFF2196F3),
                        title = "Appearance",
                        onClick = { }
                    )

                    var powerSavingOn by remember { mutableStateOf(false) }
                    SettingsItem(
                        icon = Icons.Filled.BatteryChargingFull,
                        iconColor = Color(0xFFFF9800),
                        title = "Power Saving",
                        trailing = if (powerSavingOn) "On" else "Off",
                        onClick = { }
                    )

                    SettingsItem(
                        icon = Icons.Filled.Language,
                        iconColor = Color(0xFF9C27B0),
                        title = "Language",
                        trailing = "English",
                        onClick = { }
                    )
                }

                Spacer(Modifier.height(24.dp))
            }

            // Premium/Business Group
            item {
                SettingsGroup {
                    SettingsItem(
                        icon = Icons.Filled.Star,
                        iconColor = Color(0xFF673AB7),
                        title = "Tangle Premium",
                        onClick = { }
                    )

                    SettingsItem(
                        icon = Icons.Filled.Star,
                        iconColor = Color(0xFFFFD700),
                        title = "My Stars",
                        onClick = { }
                    )

                    SettingsItem(
                        icon = Icons.Filled.Business,
                        iconColor = Color(0xFFE91E63),
                        title = "Tangle Business",
                        onClick = { }
                    )

                    SettingsItem(
                        icon = Icons.Filled.CardGiftcard,
                        iconColor = Color(0xFF00BCD4),
                        title = "Send a Gift",
                        onClick = { }
                    )
                }

                Spacer(Modifier.height(24.dp))
            }

            // Help Group
            item {
                SettingsGroup {
                    SettingsItem(
                        icon = Icons.Filled.QuestionAnswer,
                        iconColor = Color(0xFFFF9800),
                        title = "Ask a Question",
                        onClick = { }
                    )

                    SettingsItem(
                        icon = Icons.Filled.Help,
                        iconColor = Color(0xFF2196F3),
                        title = "Tangle FAQ",
                        onClick = { }
                    )

                    SettingsItem(
                        icon = Icons.Filled.Lightbulb,
                        iconColor = Color(0xFFFFC107),
                        title = "Tangle Features",
                        onClick = { }
                    )
                }

                Spacer(Modifier.height(24.dp))
            }

            // Logout
            item {
                Button(
                    onClick = { showLogoutDialog = true },
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = Color(0xFFE1306C)
                    ),
                    shape = RoundedCornerShape(12.dp)
                ) {
                    Text(
                        "Log Out",
                        modifier = Modifier.padding(vertical = 8.dp),
                        fontWeight = FontWeight.SemiBold
                    )
                }
            }
        }
    }

    if (showLogoutDialog) {
        AlertDialog(
            onDismissRequest = { showLogoutDialog = false },
            title = { Text("Log Out") },
            text = { Text("Are you sure you want to log out?") },
            confirmButton = {
                TextButton(
                    onClick = {
                        showLogoutDialog = false
                        onLogout()
                    }
                ) {
                    Text("Log Out", color = Color(0xFFE1306C))
                }
            },
            dismissButton = {
                TextButton(onClick = { showLogoutDialog = false }) {
                    Text("Cancel")
                }
            }
        )
    }
}

// ============================================
// PROFILE HEADER
// ============================================

@Composable
fun ProfileHeader() {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        // Avatar
        Box(
            modifier = Modifier
                .size(80.dp)
                .clip(CircleShape)
                .background(
                    Brush.linearGradient(
                        colors = listOf(
                            Color(0xFF667eea),
                            Color(0xFF764ba2)
                        )
                    )
                )
                .clickable { },
            contentAlignment = Alignment.Center
        ) {
            Text(
                "T",
                style = MaterialTheme.typography.headlineMedium,
                color = Color.White,
                fontWeight = FontWeight.Bold
            )

            // Camera badge
            Box(
                modifier = Modifier
                    .align(Alignment.BottomEnd)
                    .size(28.dp)
                    .background(Color(0xFF2196F3), CircleShape)
                    .border(3.dp, MaterialTheme.colorScheme.surface, CircleShape),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    Icons.Filled.CameraAlt,
                    "Edit",
                    tint = Color.White,
                    modifier = Modifier.size(16.dp)
                )
            }
        }

        Spacer(Modifier.height(16.dp))

        // Set Profile Photo
        TextButton(
            onClick = { },
            colors = ButtonDefaults.textButtonColors(
                contentColor = Color(0xFF2196F3)
            )
        ) {
            Icon(
                Icons.Outlined.CameraAlt,
                contentDescription = null,
                modifier = Modifier.size(20.dp)
            )
            Spacer(Modifier.width(8.dp))
            Text(
                "Set Profile Photo",
                fontWeight = FontWeight.Medium
            )
        }

        // Set Username
        TextButton(
            onClick = { },
            colors = ButtonDefaults.textButtonColors(
                contentColor = Color(0xFF2196F3)
            )
        ) {
            Icon(
                Icons.Outlined.AlternateEmail,
                contentDescription = null,
                modifier = Modifier.size(20.dp)
            )
            Spacer(Modifier.width(8.dp))
            Text(
                "Set Username",
                fontWeight = FontWeight.Medium
            )
        }
    }
}

// ============================================
// SETTINGS GROUP (Telegram-style card)
// ============================================

@Composable
fun SettingsGroup(
    content: @Composable ColumnScope.() -> Unit
) {
    Surface(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp),
        shape = RoundedCornerShape(16.dp),
        color = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f)
    ) {
        Column {
            content()
        }
    }
}

// ============================================
// SETTINGS ITEM
// ============================================

@Composable
fun SettingsItem(
    icon: androidx.compose.ui.graphics.vector.ImageVector,
    iconColor: Color,
    title: String,
    subtitle: String? = null,
    trailing: String? = null,
    badge: Boolean = false,
    onClick: () -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .padding(horizontal = 16.dp, vertical = 12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Icon with colored background
        Box(
            modifier = Modifier
                .size(40.dp)
                .background(iconColor, RoundedCornerShape(8.dp)),
            contentAlignment = Alignment.Center
        ) {
            Icon(
                icon,
                contentDescription = null,
                tint = Color.White,
                modifier = Modifier.size(22.dp)
            )
        }

        Spacer(Modifier.width(16.dp))

        // Title + subtitle
        Column(modifier = Modifier.weight(1f)) {
            Text(
                title,
                style = MaterialTheme.typography.bodyLarge
            )
            if (subtitle != null) {
                Text(
                    subtitle,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }

        // Badge
        if (badge) {
            Badge(
                containerColor = Color(0xFFE1306C),
                modifier = Modifier.padding(end = 8.dp)
            ) {
                Text(
                    "!",
                    style = MaterialTheme.typography.labelSmall,
                    fontWeight = FontWeight.Bold
                )
            }
        }

        // Trailing text
        if (trailing != null) {
            Text(
                trailing,
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
            Spacer(Modifier.width(8.dp))
        }

        // Arrow
        Icon(
            Icons.Default.ChevronRight,
            contentDescription = null,
            tint = MaterialTheme.colorScheme.onSurfaceVariant,
            modifier = Modifier.size(20.dp)
        )
    }
}

// ============================================
// SETTINGS ITEM WITH SWITCH
// ============================================

@Composable
fun SettingsItemSwitch(
    icon: androidx.compose.ui.graphics.vector.ImageVector,
    iconColor: Color,
    title: String,
    subtitle: String? = null,
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable { onCheckedChange(!checked) }
            .padding(horizontal = 16.dp, vertical = 12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Icon
        Box(
            modifier = Modifier
                .size(40.dp)
                .background(iconColor, RoundedCornerShape(8.dp)),
            contentAlignment = Alignment.Center
        ) {
            Icon(
                icon,
                contentDescription = null,
                tint = Color.White,
                modifier = Modifier.size(22.dp)
            )
        }

        Spacer(Modifier.width(16.dp))

        // Title and subtitle
        Column(modifier = Modifier.weight(1f)) {
            Text(
                title,
                style = MaterialTheme.typography.bodyLarge
            )
            if (subtitle != null) {
                Text(
                    subtitle,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }

        // Switch
        Switch(
            checked = checked,
            onCheckedChange = onCheckedChange,
            colors = SwitchDefaults.colors(
                checkedThumbColor = Color.White,
                checkedTrackColor = Color(0xFF4CAF50)
            )
        )
    }
}

// ============================================
// SECTION HEADER
// ============================================

@Composable
fun SectionHeader(text: String) {
    Text(
        text,
        modifier = Modifier.padding(horizontal = 32.dp),
        style = MaterialTheme.typography.labelMedium,
        color = MaterialTheme.colorScheme.primary,
        fontWeight = FontWeight.SemiBold,
        letterSpacing = 0.5.sp
    )
}
